import { useState, useEffect } from "react";
import { useStoreState } from "easy-peasy";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { getApiUrl, getAuthKey } from "./apiUtils";

/**
 * Custom hook to get the current logged-in user
 * @param {boolean} fetchFullProfile - Whether to fetch the full user profile from the server
 * @returns {Object} An object containing the current user and loading state
 */
export const useCurrentUser = (fetchFullProfile = false) => {
  const user = useStoreState((state) => state.user);
  const [fullUserProfile, setFullUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const authKey = getAuthKey();

    // If no token exists, set loading to false and return
    if (!authKey) {
      setLoading(false);
      return;
    }

    // Parse the token to get basic user information
    try {
      const decoded = jwtDecode(authKey);

      // If full profile is requested, fetch it from the server
      if (fetchFullProfile) {
        const fetchFullProfile = async () => {
          try {
            const response = await axios.get(
              `${getApiUrl()}/users/profile/${decoded.id}`,
              {
                headers: { Authorization: `Bearer ${authKey}` },
              }
            );

            setFullUserProfile(response.data.user);
            setLoading(false);
          } catch (err) {
            setError(err.response?.data?.message || err.message);
            setLoading(false);
          }
        };

        fetchFullProfile();
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError("Invalid authentication token");
      setLoading(false);
    }
  }, [fetchFullProfile]);

  return {
    user, // Basic user info from the store
    fullProfile: fullUserProfile, // Full user profile from the server
    loading,
    error,
  };
};
